import { useState } from 'react';
import { db } from '../../db/index.js';
import { updateDeckConfig } from '../../db/repo/decks.js';
import { resolveConfig, DEFAULT_DECK_CONFIG } from '../../scheduler/fsrs.js';
import Sheet from '../../components/Sheet.jsx';
import Button from '../../components/Button.jsx';
import Icon from '../../components/Icon.jsx';
import { TextInput, Toggle, Stepper, ChoiceCards, Group } from '../../components/Field.jsx';
import { toast } from '../../components/Toaster.jsx';
import styles from './DeckSheets.module.scss';

const RETENTION = [
  { value: 0.85, label: 'Relaxed', description: 'Fewer reviews. You’ll forget a little more.' },
  { value: 0.9, label: 'Balanced', description: 'Recommended for most people.' },
  { value: 0.95, label: 'Thorough', description: 'More reviews. You’ll remember almost everything.' },
];

const NEW_ORDER = [
  { value: 'mix', label: 'Mixed in', description: 'New cards appear between reviews.' },
  { value: 'after', label: 'After reviews', description: 'Finish reviews first, then new cards.' },
  { value: 'before', label: 'Before reviews', description: 'Start with new cards.' },
];

export default function DeckOptionsSheet(props) {
  return props.open ? <DeckOptions {...props} /> : null;
}

function DeckOptions({ open, onClose, deck }) {
  const [cfg, setCfg] = useState(() => resolveConfig(deck?.config));
  const [advanced, setAdvanced] = useState(false);

  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));
  const retentionPct = Math.round(Number(cfg.retention) * 100);
  const preset = RETENTION.find((r) => Math.round(r.value * 100) === retentionPct)?.value ?? null;

  const save = async (e) => {
    e?.preventDefault();
    const current = resolveConfig(deck.config);
    const clean = {
      newPerDay: Math.max(0, Math.round(Number(cfg.newPerDay) || 0)),
      reviewsPerDay: Math.max(0, Math.round(Number(cfg.reviewsPerDay) || 0)),
      retention: Math.min(0.99, Math.max(0.7, Number(cfg.retention) || 0.9)),
      maxInterval: Math.max(1, Math.round(Number(cfg.maxInterval) || 36500)),
      learningSteps: String(cfg.learningSteps).trim() || DEFAULT_DECK_CONFIG.learningSteps,
      relearningSteps: String(cfg.relearningSteps).trim() || DEFAULT_DECK_CONFIG.relearningSteps,
      fuzz: !!cfg.fuzz,
      newOrder: cfg.newOrder,
    };
    const changed = Object.fromEntries(Object.entries(clean).filter(([k, v]) => current[k] !== v));
    if (Object.keys(changed).length) {
      await updateDeckConfig(db, deck.id, changed);
      toast.success('Study settings saved');
    }
    onClose();
  };

  if (!deck) return null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Study settings"
      subtitle={`For “${deck.name}” and its subdecks`}
      footer={
        <>
          <Button size="lg" onClick={() => setCfg({ ...DEFAULT_DECK_CONFIG })}>
            Reset
          </Button>
          <Button size="lg" variant="primary" type="submit" form="deck-options">
            Save
          </Button>
        </>
      }
    >
      <form id="deck-options" onSubmit={save}>
        <Group title="Each day" footer="Limits keep your daily workload steady.">
          <Stepper
            label="New cards"
            hint="Cards you haven’t seen before"
            value={cfg.newPerDay}
            onChange={set('newPerDay')}
            min={0}
            max={9999}
            step={5}
          />
          <Stepper
            label="Reviews, at most"
            hint="Cards you’ve seen and need to refresh"
            value={cfg.reviewsPerDay}
            onChange={set('reviewsPerDay')}
            min={0}
            max={9999}
            step={10}
          />
        </Group>

        <ChoiceCards
          label="How well do you want to remember?"
          value={preset}
          onChange={set('retention')}
          options={RETENTION}
        />

        <ChoiceCards label="When should new cards appear?" value={cfg.newOrder} onChange={set('newOrder')} options={NEW_ORDER} size="sm" />

        <button type="button" className={styles.disclosure} aria-expanded={advanced} onClick={() => setAdvanced((a) => !a)}>
          <span>
            <strong>Advanced</strong>
            <small>Fine-tune the scheduler</small>
          </span>
          <Icon name="down" size={18} className={advanced ? styles.flip : undefined} />
        </button>

        {advanced ? (
          <div className={styles.advanced}>
            <Group>
              <Stepper
                label="Target memory"
                hint="Chance you’ll remember a card when it’s due"
                value={retentionPct}
                onChange={(v) => set('retention')(Number(v) / 100)}
                min={70}
                max={99}
                suffix="%"
              />
              <Stepper
                label="Longest gap"
                hint="Never wait longer than this between reviews"
                value={cfg.maxInterval}
                onChange={set('maxInterval')}
                min={1}
                max={36500}
                step={30}
                suffix="days"
              />
              <Toggle
                label="Spread out reviews"
                hint="Avoids cards added together all coming back on the same day."
                checked={cfg.fuzz}
                onChange={set('fuzz')}
              />
            </Group>
            <TextInput
              label="Learning steps"
              value={cfg.learningSteps}
              onChange={(e) => set('learningSteps')(e.target.value)}
              hint="Waits between first tries of a new card. Use m for minutes, h for hours, d for days, e.g. “1m 10m”."
              autoCapitalize="off"
              autoCorrect="off"
            />
            <TextInput
              label="Relearning steps"
              value={cfg.relearningSteps}
              onChange={(e) => set('relearningSteps')(e.target.value)}
              hint="Waits after you forget a card, e.g. “10m”."
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>
        ) : null}
      </form>
    </Sheet>
  );
}
